import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../App.css';

import Loader from '../components/Loader';
import { AlertIcon, ChatIcon, MailIcon } from '../components/Icons';
import { BASE_URL } from '../App';
import { apiErrorMessage } from '../utils/format';
import {
  trackPaymentInitiated,
  trackPaymentAbandoned,
  trackPaymentResultViewed,
  useTrackError,
} from '../utils/analytics';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
const JUSPAY_SDK_SRC =
  'https://public.releases.juspay.in/hyper-sdk-web/juspay-hyper-sdk-web.umd.js';

const Processing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderResponse = location.state?.orderResponse;
  const orderId = orderResponse?.order_id;

  const [isInitiating, setIsInitiating] = useState(false);
  const hasInitiatedRef = useRef(false);
  const [paymentGateway, setPaymentGateway] = useState(null); // 'razorpay' | 'juspay'
  const [jusPayIframeUrl, setJusPayIframeUrl] = useState('');
  const [jusPaySdkPayload, setJusPaySdkPayload] = useState(null);
  const [showJusPayModal, setShowJusPayModal] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [initError, setInitError] = useState('');

  useTrackError('payment_initiation', initError);
  useTrackError('processing', orderId ? null : 'no_order_in_state');

  const orderAmount = orderResponse?.amount;
  const orderCurrency = orderResponse?.currency;

  const loadScript = (src) =>
    new Promise((resolve) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const initiateRazorpayPayment = useCallback(
    async (checkoutData) => {
      const loaded = await loadScript(RAZORPAY_SRC);
      if (!loaded) throw new Error('Razorpay SDK failed to load');

      const paymentObject = new window.Razorpay({
        ...checkoutData,
        handler: () => {
          window.location.href = `/payment-callback?order_id=${orderId}`;
        },
        modal: {
          ondismiss: () => {
            trackPaymentAbandoned({
              orderId,
              gateway: 'razorpay',
              stage: 'gateway_dismissed',
            });
            setIsInitiating(false);
            hasInitiatedRef.current = false;
            setShowRetryButton(true);
          },
        },
        // Brand teal, matching the marketplace CTA.
        theme: { color: '#15a892' },
      });

      paymentObject.open();
      trackPaymentInitiated({
        orderId,
        amount: orderAmount,
        currency: orderCurrency,
        gateway: 'razorpay',
      });
    },
    [orderId, orderAmount, orderCurrency]
  );

  const initiatePayment = useCallback(async () => {
    if (hasInitiatedRef.current || !orderId) return;

    try {
      setIsInitiating(true);
      hasInitiatedRef.current = true;
      setShowRetryButton(false);
      setInitError('');

      const response = await fetch(`${BASE_URL}/partner/v2/initiate-portal-payment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('portalToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_id: orderId }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          apiErrorMessage(data, 'We could not start the payment for this order.')
        );
      }

      // JusPay may report the order as already paid.
      if (data.result?.status === 'CHARGED') {
        window.location.href = `/payment-callback?order_id=${orderId}`;
        return;
      }

      if (data.result?.checkout_data) {
        setPaymentGateway('razorpay');
        await initiateRazorpayPayment(data.result.checkout_data);
      } else if (data.result?.sdk_payload) {
        setPaymentGateway('juspay');
        setJusPaySdkPayload(data.result.sdk_payload);
        if (data.result.payment_links?.web) {
          setJusPayIframeUrl(data.result.payment_links.web);
        }
        setTimeout(() => setShowJusPayModal(true), 100);
        trackPaymentInitiated({
          orderId,
          amount: orderAmount,
          currency: orderCurrency,
          gateway: 'juspay',
        });
      } else if (data.result?.payment_links?.iframe) {
        setPaymentGateway('juspay');
        setJusPayIframeUrl(data.result.payment_links.iframe);
        setTimeout(() => setShowJusPayModal(true), 100);
        trackPaymentInitiated({
          orderId,
          amount: orderAmount,
          currency: orderCurrency,
          gateway: 'juspay',
        });
      } else {
        throw new Error('The payment gateway sent back an unexpected response.');
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      setIsInitiating(false);
      hasInitiatedRef.current = false;
      setInitError(error.message || 'Payment could not be started.');
      setShowRetryButton(true);
    }
  }, [initiateRazorpayPayment, orderId, orderAmount, orderCurrency]);

  const closeJusPayModal = () => {
    setShowJusPayModal(false);
    setTimeout(() => {
      setIsInitiating(false);
      hasInitiatedRef.current = false;
      setPaymentGateway(null);
      setJusPayIframeUrl('');
      setJusPaySdkPayload(null);
      setShowRetryButton(true);
    }, 320); // matches the sheet transition
  };

  // Offer a way out if the gateway never shows up.
  useEffect(() => {
    if (!isInitiating || showRetryButton) return undefined;
    const timer = setTimeout(() => setShowRetryButton(true), 10000);
    return () => clearTimeout(timer);
  }, [isInitiating, showRetryButton]);

  useEffect(() => {
    if (orderId && !hasInitiatedRef.current) initiatePayment();
  }, [orderId, initiatePayment]);

  // JusPay posts payment outcomes back to the opener.
  useEffect(() => {
    if (paymentGateway !== 'juspay') return undefined;

    const onMessage = (event) => {
      try {
        const payload =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!payload || typeof payload !== 'object') return;

        if (payload.status === 'SUCCESS' || payload.status === 'CHARGED') {
          window.location.href = `/payment-callback?order_id=${orderId}`;
        } else if (payload.status === 'FAILED') {
          trackPaymentResultViewed({ orderId, status: 'failed', reason: 'gateway_failed' });
          closeJusPayModal();
        } else if (payload.status === 'CANCELLED') {
          trackPaymentAbandoned({ orderId, gateway: 'juspay', stage: 'gateway_cancelled' });
          closeJusPayModal();
        }
      } catch {
        /* not a JusPay message */
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [paymentGateway, orderId]);

  useEffect(() => {
    if (paymentGateway === 'juspay' && jusPaySdkPayload && !window.JuspayHyperSDK) {
      loadScript(JUSPAY_SDK_SRC);
    }
  }, [paymentGateway, jusPaySdkPayload]);

  /* ---------------- Render ---------------- */

  // Landing here directly (refresh, back button, shared link) leaves no order
  // in router state. This used to sit on a spinner with no way forward.
  if (!orderId) {
    return (
      <div className="page">
        <div className="statusWrap">
          <div className="statusCard">
            <div className="statusIcon statusIconBad" aria-hidden="true">
              <AlertIcon width="28" height="28" />
            </div>
            <h1 className="statusTitle">No order in progress</h1>
            <p className="statusBody">
              We couldn't find an order to pay for. If you were part-way through a
              purchase, start it again from the marketplace — you have not been
              charged.
            </p>
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={() => navigate('/marketplace')}
              >
                Back to marketplace
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="statusWrap">
        <div className="statusCard">
          {initError ? (
            <div className="statusIcon statusIconBad" aria-hidden="true">
              <AlertIcon width="28" height="28" />
            </div>
          ) : (
            <Loader size={120} />
          )}

          <h1 className="statusTitle">
            {initError ? 'Payment could not start' : 'Opening secure checkout'}
          </h1>
          <p className="statusBody">
            {initError ||
              'Hold on — we are handing you over to the payment gateway. Do not close this tab.'}
          </p>
          <div className="statusMeta">Order ID: {orderId}</div>

          {showRetryButton && (
            <>
              {!initError && (
                <p className="statusBody">This is taking longer than usual.</p>
              )}
              <div className="statusActions">
                <button
                  type="button"
                  className={cx('btn', 'btnMd', 'btnBrand')}
                  onClick={() => {
                    setIsInitiating(false);
                    hasInitiatedRef.current = false;
                    initiatePayment();
                  }}
                >
                  Try payment again
                </button>
                <button
                  type="button"
                  className={cx('btn', 'btnMd', 'btnSecondary')}
                  onClick={() => navigate('/marketplace')}
                >
                  Back to marketplace
                </button>
              </div>

              <div className="supportRow" style={{ justifyContent: 'center' }}>
                <a
                  className={cx('btn', 'btnSm', 'btnSecondary')}
                  href={`https://wa.me/919148477518?text=${encodeURIComponent(
                    `Payment issue with order ${orderId}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ChatIcon />
                  WhatsApp
                </a>
                <a
                  className={cx('btn', 'btnSm', 'btnSecondary')}
                  href={`mailto:help@getfleek.app?subject=${encodeURIComponent(
                    `Payment issue - Order ID: ${orderId}`
                  )}`}
                >
                  <MailIcon />
                  Email
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* JusPay sheet */}
      {paymentGateway === 'juspay' && jusPayIframeUrl && (
        <div
          className={cx('modalOverlay', showJusPayModal && 'modalOverlayOpen')}
          style={{ pointerEvents: showJusPayModal ? 'auto' : 'none' }}
          role="dialog"
          aria-modal="true"
          aria-label="Complete payment"
        >
          <div className="modalSheet">
            <div className="modalHead">
              <span className="modalTitle">Complete payment</span>
              <button
                type="button"
                className="modalClose"
                onClick={() => {
                  trackPaymentAbandoned({
                    orderId,
                    gateway: 'juspay',
                    stage: 'gateway_closed',
                  });
                  closeJusPayModal();
                }}
                aria-label="Close payment"
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <iframe
                className="modalFrame"
                src={jusPayIframeUrl}
                title="Payment"
                allow="payment"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Processing;
